import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import FilePicker from "../components/FilePicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function ShareSign() {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [shares, setShares] = useState([]);
  const [latest, setLatest] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/shares");
      setShares(data);
    } catch (e) {
      // not fatal
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function create() {
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await api.post("/share/create", {
        file_id: file.id,
        recipient_name: recipient,
        recipient_note: note,
      });
      const fullUrl = `${window.location.origin}/sign/${data.token}`;
      setLatest({ ...data, fullUrl });
      await load();
      toast.success(t("share_link_ready"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  async function revoke(token) {
    try {
      await api.post(`/shares/${token}/revoke`);
      await load();
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  function copy(url) {
    navigator.clipboard.writeText(url);
    toast.success(t("share_copy"));
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12" data-testid="share-sign-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_share_sign")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_share_sign_desc")}</p>

      <div className="mt-8 grid gap-8">
        {!file ? (
          <FilePicker accept="application/pdf" filter={(f) => f.ext === "pdf"} onSelected={(f) => setFile(f)} />
        ) : (
          <div className="border border-zinc-200 rounded-md p-6 bg-white grid gap-4">
            <div>
              <span className="eyebrow">FILE</span>
              <div className="hero-h2 text-xl mt-1">{file.original_filename}</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="eyebrow">{t("share_recipient")}</Label>
                <Input value={recipient} onChange={(e) => setRecipient(e.target.value)}
                  className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="share-recipient" />
              </div>
              <div>
                <Label className="eyebrow">{t("share_note")}</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)}
                  className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="share-note" />
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={create} disabled={busy}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11" data-testid="share-create">
                {busy ? t("processing") : `${t("share_create")} →`}
              </Button>
              <Button variant="ghost" className="rounded-sm h-11" onClick={() => { setFile(null); setLatest(null); }}>
                {t("cta_clear")}
              </Button>
            </div>
          </div>
        )}

        {latest && (
          <div className="border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="share-link-card">
            <span className="eyebrow">{t("share_link_ready")}</span>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <code className="bg-white border border-zinc-200 rounded-sm px-3 py-2 text-sm break-all" data-testid="share-link-url">
                {latest.fullUrl}
              </code>
              <Button onClick={() => copy(latest.fullUrl)}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10" data-testid="share-copy-btn">
                ⎘ {t("share_copy")}
              </Button>
            </div>
          </div>
        )}

        <div>
          <h2 className="hero-h2 text-2xl">{t("share_my_links")}</h2>
          <div className="mt-4 border border-zinc-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  <th className="px-4 py-3 eyebrow">Token</th>
                  <th className="px-4 py-3 eyebrow">{t("share_recipient")}</th>
                  <th className="px-4 py-3 eyebrow">Status</th>
                  <th className="px-4 py-3 eyebrow text-right">{t("th_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {shares.length === 0 && (
                  <tr><td colSpan="4" className="px-4 py-10 text-center text-zinc-500">{t("share_no_links")}</td></tr>
                )}
                {shares.map((s) => {
                  const url = `${window.location.origin}/sign/${s.token}`;
                  return (
                    <tr key={s.token} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50" data-testid={`share-row-${s.token}`}>
                      <td className="px-4 py-3 mono text-xs text-zinc-500 truncate max-w-[150px]">{s.token.slice(0, 12)}…</td>
                      <td className="px-4 py-3">{s.recipient_name || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="pill">{t(`share_${s.status}`)}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" className="rounded-sm h-8 px-3" onClick={() => copy(url)}>
                          ⎘
                        </Button>
                        {s.status === "pending" && (
                          <Button variant="ghost" className="rounded-sm h-8 px-3 text-red-600 hover:text-red-700"
                            onClick={() => revoke(s.token)} data-testid={`revoke-${s.token}`}>
                            {t("share_revoke")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
